import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { HotlineCallsTable } from '@/components/HotlineCallsTable'
import { Accordion, Text } from '@mantine/core'
import dayjs from 'dayjs'
import { DateRangePicker } from '@/components/DateRangePicker'
import { TbCalendarTime } from 'react-icons/tb'
import PageShell from '@/components/Common/PageShell'
import { requirePermission } from '@/utils/auth'
import { formatDateForUrl } from '@/utils/datetime'
import { parseAndValidateDates } from '@/utils/datetime'
import { useTranslation } from 'react-i18next'
import { useState } from 'react'

const ROUTE_PATH = '/hotline-calls/$startDate/$endDate'

const HotlineCalls = () => {
  const { startDate: startDateParam, endDate: endDateParam } = Route.useParams()
  const navigate = useNavigate()
  const { t } = useTranslation('hotline')
  const [isRangeSelectionInProgress, setIsRangeSelectionInProgress] = useState(false)

  const navigateToDateRange = (newStartDate: Date, newEndDate: Date) => {
    navigate({
      to: ROUTE_PATH,
      params: {
        startDate: formatDateForUrl(newStartDate),
        endDate: formatDateForUrl(newEndDate),
      },
    })
  }

  const { startDate, endDate } = parseAndValidateDates(startDateParam, endDateParam)

  const setStartDate = (date: Date | null) => {
    if (date) {
      if (dayjs(date).isAfter(endDate)) {
        const newEndDate = dayjs(date).add(1, 'day').toDate()
        navigateToDateRange(date, newEndDate)
      } else {
        navigateToDateRange(date, endDate)
      }
    }
  }

  const setEndDate = (date: Date | null) => {
    if (date) {
      if (dayjs(date).isBefore(startDate)) {
        const newStartDate = dayjs(date).subtract(1, 'day').toDate()
        navigateToDateRange(newStartDate, date)
      } else {
        navigateToDateRange(startDate, date)
      }
    }
  }

  return (
    <PageShell title={t('hotlineCallsOverview')}>
      <Accordion variant="filled" radius="md" mb="sm">
        <Accordion.Item value="dateRange">
          <Accordion.Control icon={<TbCalendarTime size={22} color="var(--mantine-color-dimmed)" />}>
            {t('selectDateRange', { ns: 'common' })}
          </Accordion.Control>
          <Accordion.Panel>
            <DateRangePicker
              startDate={startDate}
              endDate={endDate}
              onStartDateChange={(date: Date | null) => setStartDate(date)}
              onEndDateChange={(date: Date | null) => setEndDate(date)}
              onRangeChange={(startDate: Date | null, endDate: Date | null) => {
                if (startDate && endDate) {
                  navigateToDateRange(startDate, endDate)
                }
              }}
              onRangeSelectionStateChange={setIsRangeSelectionInProgress}
              mb="xl"
            />
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>
      {isRangeSelectionInProgress ? (
        <Text ta="center" c="dimmed" py="xl" fz="xl" fw={600}>
          {t('selectRangeToSeeData', { ns: 'common' })}
        </Text>
      ) : (
        <HotlineCallsTable startDate={startDate} endDate={endDate} tableKey="hotlineTableColumns" />
      )}
    </PageShell>
  )
}

export const Route = createFileRoute('/hotline-calls/$startDate/$endDate')({
  beforeLoad: () => requirePermission('hotline'),
  component: HotlineCalls,
  parseParams: params => ({
    startDate: params.startDate,
    endDate: params.endDate,
  }),
})

export default HotlineCalls
